<!-- eslint-disable max-len -->
<template>
  <transition name="banner-slide">
    <div v-if="visible" class="cookie-banner" role="dialog" aria-label="Aviso de cookies">
      <div class="cookie-banner__content">
        <div class="cookie-banner__text">
          <p class="cookie-banner__title">Usamos cookies</p>
          <p class="cookie-banner__desc">
            Utilizamos cookies propias y de terceros (Google Analytics, Meta Pixel, TikTok) para mejorar tu
            experiencia y analizar el uso del sitio. Puedes aceptar todas, personalizar tu elección o rechazar
            las no esenciales.
            <router-link to="/politica-de-cookies" class="cookie-banner__link">Más información</router-link>
          </p>
        </div>

        <!-- Expanded preferences -->
        <div v-if="showPreferences" class="cookie-banner__prefs">
          <label class="cookie-pref">
            <span class="cookie-pref__info">
              <span class="cookie-pref__name">Necesarias</span>
              <span class="cookie-pref__desc">Esenciales para el funcionamiento del sitio.</span>
            </span>
            <span class="cookie-pref__toggle cookie-pref__toggle--disabled">Siempre activas</span>
          </label>
          <label class="cookie-pref">
            <span class="cookie-pref__info">
              <span class="cookie-pref__name">Analíticas</span>
              <span class="cookie-pref__desc">Google Analytics — estadísticas de uso.</span>
            </span>
            <input type="checkbox" v-model="consent.analytics" class="cookie-pref__checkbox" />
            <span class="cookie-pref__slider" :class="{ active: consent.analytics }"></span>
          </label>
          <label class="cookie-pref">
            <span class="cookie-pref__info">
              <span class="cookie-pref__name">Marketing</span>
              <span class="cookie-pref__desc">Meta Pixel, Google Ads, TikTok Pixel.</span>
            </span>
            <input type="checkbox" v-model="consent.marketing" class="cookie-pref__checkbox" />
            <span class="cookie-pref__slider" :class="{ active: consent.marketing }"></span>
          </label>
        </div>

        <div class="cookie-banner__actions">
          <button class="cookie-btn cookie-btn--outline" @click="togglePreferences">
            {{ showPreferences ? 'Ocultar opciones' : 'Personalizar' }}
          </button>
          <button class="cookie-btn cookie-btn--secondary" @click="rejectOptional">
            Solo necesarias
          </button>
          <button class="cookie-btn cookie-btn--primary" @click="acceptAll">
            Aceptar todas
          </button>
        </div>

        <div v-if="showPreferences" class="cookie-banner__save">
          <button class="cookie-btn cookie-btn--primary" @click="savePreferences">
            Guardar preferencias
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
const STORAGE_KEY = 'vivekonecta_cookie_consent';

export default {
  name: 'CookieBanner',
  data() {
    return {
      visible: false,
      showPreferences: false,
      consent: {
        analytics: true,
        marketing: true,
      },
    };
  },
  mounted() {
    if (process.env.VUE_APP_ENABLE_COOKIES !== 'true') return;
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) {
      setTimeout(() => { this.visible = true; }, 800);
    } else {
      this.applyConsent(JSON.parse(saved));
    }
  },
  methods: {
    acceptAll() {
      this.save({ analytics: true, marketing: true });
    },
    rejectOptional() {
      this.save({ analytics: false, marketing: false });
    },
    savePreferences() {
      this.save({ ...this.consent });
    },
    save(prefs) {
      const payload = { ...prefs, timestamp: Date.now() };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
      this.applyConsent(payload);
      this.visible = false;
    },
    applyConsent(prefs) {
      // Google Analytics consent
      if (window.gtag) {
        window.gtag('consent', 'update', {
          analytics_storage: prefs.analytics ? 'granted' : 'denied',
          ad_storage: prefs.marketing ? 'granted' : 'denied',
          ad_user_data: prefs.marketing ? 'granted' : 'denied',
          ad_personalization: prefs.marketing ? 'granted' : 'denied',
        });
      }
    },
    togglePreferences() {
      this.showPreferences = !this.showPreferences;
    },
  },
};
</script>

<style scoped lang="scss">
$blue:      #1300d4;
$dark-blue: #0f0f72;
$white:     #ffffff;
$font:      'Poppins', 'Barlow', sans-serif;

.cookie-banner {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 9999;
  background: $white;
  box-shadow: 0 -4px 32px rgba(0, 0, 0, 0.12);
  border-top: 3px solid $blue;
  font-family: $font;
}

.cookie-banner__content {
  max-width: 1100px;
  margin: 0 auto;
  padding: 20px 24px;
}

.cookie-banner__text {
  margin-bottom: 16px;
}

.cookie-banner__title {
  font-size: 1rem;
  font-weight: 700;
  color: $dark-blue;
  margin: 0 0 6px;
}

.cookie-banner__desc {
  font-size: 0.875rem;
  color: #444;
  line-height: 1.6;
  margin: 0;
}

.cookie-banner__link {
  color: $blue;
  font-weight: 600;
  text-decoration: underline;
  white-space: nowrap;
}

/* Preferences */
.cookie-banner__prefs {
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: #f7f7fb;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
}

.cookie-pref {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  position: relative;

  &__info {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__name {
    font-size: 0.875rem;
    font-weight: 600;
    color: $dark-blue;
  }

  &__desc {
    font-size: 0.8rem;
    color: #666;
  }

  &__toggle--disabled {
    font-size: 0.75rem;
    font-weight: 600;
    color: #28a745;
    white-space: nowrap;
  }

  &__checkbox {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
  }

  &__slider {
    flex-shrink: 0;
    width: 44px;
    height: 24px;
    background: #ccc;
    border-radius: 12px;
    transition: background 0.25s;
    position: relative;
    cursor: pointer;

    &::after {
      content: '';
      position: absolute;
      width: 18px;
      height: 18px;
      background: $white;
      border-radius: 50%;
      top: 3px;
      left: 3px;
      transition: transform 0.25s;
      box-shadow: 0 1px 3px rgba(0,0,0,0.2);
    }

    &.active {
      background: $blue;

      &::after {
        transform: translateX(20px);
      }
    }
  }
}

/* Actions */
.cookie-banner__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

.cookie-banner__save {
  margin-top: 10px;
}

.cookie-btn {
  border: none;
  border-radius: 6px;
  padding: 9px 20px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  font-family: $font;
  transition: all 0.2s ease;

  &--primary {
    background: $blue;
    color: $white;

    &:hover { background: $dark-blue; }
  }

  &--secondary {
    background: transparent;
    color: $blue;
    border: 2px solid $blue;

    &:hover { background: $blue; color: $white; }
  }

  &--outline {
    background: transparent;
    color: #555;
    border: 1px solid #ccc;

    &:hover { border-color: $blue; color: $blue; }
  }
}

/* Animation */
.banner-slide-enter-active,
.banner-slide-leave-active {
  transition: transform 0.35s ease, opacity 0.35s ease;
}

.banner-slide-enter,
.banner-slide-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

@media (max-width: 600px) {
  .cookie-banner__actions {
    flex-direction: column;
    align-items: stretch;

    .cookie-btn { text-align: center; }
  }
}
</style>
